import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  createDirectoryCategory, updateDirectoryCategory, deleteDirectoryCategory,
  createDirectoryTag, deleteDirectoryTag,
} from "../../services/api";

export const DirectoryManagerDialog = ({ open, onClose, categories, tags, onChanged }) => {
  const [newCat, setNewCat] = useState("");
  const [newCatLoc, setNewCatLoc] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [editName, setEditName] = useState("");
  const [newTag, setNewTag] = useState("");

  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      await createDirectoryCategory({ name: newCat.trim(), is_location: newCatLoc });
      setNewCat(""); setNewCatLoc(false); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const saveCat = async (c) => {
    try {
      await updateDirectoryCategory(c.id, { name: editName.trim(), is_location: c.is_location });
      setEditCat(null); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const removeCat = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try { await deleteDirectoryCategory(c.id); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    try { await createDirectoryTag({ name: newTag.trim() }); setNewTag(""); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const removeTag = async (t) => {
    if (!window.confirm(`Supprimer le tag « ${t.name} » ? Il sera retiré de toutes les fiches.`)) return;
    try { await deleteDirectoryTag(t.id); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="directory-manager-dialog">
        <DialogHeader><DialogTitle>Catégories & tags</DialogTitle></DialogHeader>

        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Catégories</p>
          <div className="space-y-1 mb-2">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid={`manage-cat-${c.id}`}>
                {editCat === c.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-xs flex-1" />
                    <button className="text-emerald-600" onClick={() => saveCat(c)}><Check size={14} /></button>
                    <button className="text-slate-400" onClick={() => setEditCat(null)}><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-medium text-slate-700 flex-1">
                      {c.name} {c.is_location && <span className="text-[9px] text-[#26788A] font-semibold ml-1">LIEU</span>}
                      <span className="text-slate-400 font-normal ml-1">({c.contacts_count || 0})</span>
                    </span>
                    <button className="text-slate-300 hover:text-[#26788A]" onClick={() => { setEditCat(c.id); setEditName(c.name); }}><Pencil size={13} /></button>
                    <button className="text-slate-300 hover:text-red-500" onClick={() => removeCat(c)} data-testid={`delete-cat-${c.id}`}><Trash2 size={13} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nouvelle catégorie" className="h-8 text-xs flex-1"
              onKeyDown={(e) => e.key === "Enter" && addCat()} data-testid="new-category-input" />
            <label className="flex items-center gap-1 text-[10px] text-slate-500 whitespace-nowrap">
              <Checkbox checked={newCatLoc} onCheckedChange={setNewCatLoc} /> Lieu
            </label>
            <Button size="sm" className="h-8 bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={addCat} data-testid="add-category-btn"><Plus size={13} /></Button>
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Tags</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((t) => (
              <span key={t.id} className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full" data-testid={`manage-tag-${t.id}`}>
                {t.name}
                <button className="text-slate-400 hover:text-red-500" onClick={() => removeTag(t)}><X size={11} /></button>
              </span>
            ))}
            {tags.length === 0 && <p className="text-xs text-slate-400">Aucun tag pour l'instant.</p>}
          </div>
          <div className="flex gap-2">
            <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nouveau tag (ex : Voix-off, Dabka…)" className="h-8 text-xs flex-1"
              onKeyDown={(e) => e.key === "Enter" && addTag()} data-testid="manager-new-tag-input" />
            <Button size="sm" className="h-8 bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={addTag} data-testid="manager-add-tag-btn"><Plus size={13} /></Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
